// Deterministic Brand Name Generator
// No deps. Paste into a .ts file and call generateName("Start Cooling Inc")

/** --- Utilities: hash + PRNG (xorshift32) --- */
function fnv1a32(str: string): number {
    let h = 0x811c9dc5 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}

class RNG {
    private state: number;

    constructor(seed: number) {
        this.state = seed || 1;
        if (this.state === 0) this.state = 0x9e3779b9;
    }

    nextU32(): number {
        // xorshift32
        let x = this.state >>> 0;
        x ^= x << 13;
        x ^= x >>> 17;
        x ^= x << 5;
        this.state = x >>> 0;
        return this.state;
    }

    next(): number {
        return (this.nextU32() >>> 0) / 0xffffffff;
    }

    pick<T>(arr: T[]): T {
        return arr[Math.floor(this.next() * arr.length)];
    }

    pickIndex(len: number): number {
        return Math.floor(this.next() * len);
    }
}

/** --- Simple helpers --- */
function titleCase(s: string): string {
    return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

function normalizeSource(s: string): string {
    return s.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

/** --- Memoization --- */
const generatorCache: Record<string, Record<string, string>> = {};

function getMemoized(type: string, source: string, compute: () => string): string {
    if (!generatorCache[type]) generatorCache[type] = {};
    const normalized = normalizeSource(source);
    if (generatorCache[type][normalized]) return generatorCache[type][normalized];
    const result = compute();
    generatorCache[type][normalized] = result;
    return result;
}

/** --- Theme detection --- */
type Theme = "cooling" | "heating" | "hvac" | "plumbing" | "electric" | "general";
const themeKeywords: Record<Theme, string[]> = {
    cooling: ["cool", "chill", "ac", "a/c", "aircon", "refrig", "vent", "cold", "freeze"],
    heating: ["heat", "warm", "boiler", "furnace", "radiat"],
    hvac: ["hvac", "duct", "air", "ventilation"],
    plumbing: ["plumb", "pipe", "drain", "sewer", "water", "tap"],
    electric: ["electr", "volt", "watt", "amp", "wire", "circuit"],
    general: ["service", "inc", "llc", "ltd", "co", "group", "solutions", "works"],
};

function detectTheme(src: string): Theme {
    const s = normalizeSource(src);
    const entries = Object.entries(themeKeywords) as [Theme, string[]][];
    for (const [theme, keys] of entries) {
        if (keys.some((k) => s.includes(k))) return theme;
    }
    // heuristic: mention "cool" or "heat" words hidden in name
    if (/\b(cool|cold|ice|freeze)\b/.test(s)) return "cooling";
    if (/\b(heat|warm|boil|furnace|radiat)\b/.test(s)) return "heating";
    return "general";
}

/** --- Word banks --- */
const adjectivesGeneric = [
    "Midnight", "Apex", "Summit", "Prime", "Northstar", "Bluebird", "Silverline",
    "Amber", "Crimson", "Evergreen", "Beacon", "Zenith", "Sterling", "Vanguard",
];

const adjectivesHeat = ["Blazing", "Radiant", "Ember", "Phoenix", "Molten", "Solar", "Crimson"];
const nounsHeatPlural = ["Heaters", "Radiators", "Furnaces", "Flames", "Boilers", "Torches"];

const adjectivesCool = ["Glacial", "Polar", "Frosty", "Crystal", "Aurora", "Nimbus", "Silver"];
const nounsCoolPlural = ["Chillers", "Freezers", "Coolers", "Iceworks", "Vents", "Condensers"];

const adjectivesHVAC = ["Ducted", "Airstream", "Venturi", "Cirrus", "Skyline", "Vector"];
const nounsHVACPlural = ["Systems", "Airworks", "Ventworks", "Airflows", "Ductworks", "Controls"];

const adjectivesPlumb = ["Copper", "Ironclad", "TrueFlow", "BluePipe", "Clearline", "Everflow"];
const nounsPlumbPlural = ["Pipes", "Plumbers", "Fittings", "Drains", "Valves", "Fixtures"];

const adjectivesElectric = ["Neon", "Arc", "Spark", "Quantum", "Ion", "Voltline", "Lumen"];
const nounsElectricPlural = ["Electrics", "Circuits", "Wires", "Relays", "Transformers", "Switches"];

const firstNames = [
    "Kenny", "Riley", "Avery", "Jordan", "Casey", "Taylor", "Jamie", "Alex",
    "Quinn", "Morgan", "Charlie", "Drew", "Emery", "Hayden", "Parker",
    "Sam", "Robin", "Skylar", "Phoenix", "Dakota", "Jessie", "Skyler", "Reese",
    "Rowan", "Logan", "Emerson", "Finley", "Harper", "Marley", "Sage", "Oakley",
    "River", "Winter", "Indigo", "Justice", "Legacy", "Lyric", "True", "Zen",
    "Atlas", "Orion", "Nova", "Luna", "Solaris", "Arlo", "Ezra", "Kai", "Jude", "Miles"
];

const lastNames = [
    "Smith", "Johnson", "Williams", "Brown", "Jones", "Garcia", "Miller", "Davis",
    "Rodriguez", "Martinez", "Hernandez", "Lopez", "Gonzalez", "Wilson", "Anderson",
    "Thomas", "Taylor", "Moore", "Jackson", "Martin", "Lee", "Perez", "Thompson",
    "White", "Harris", "Sanchez", "Clark", "Ramirez", "Lewis", "Robinson", "Walker",
    "Young", "Allen", "King", "Wright", "Scott", "Torres", "Nguyen", "Hill", "Flores",
    "Green", "Adams", "Nelson", "Baker", "Hall", "Rivera", "Campbell", "Mitchell",
    "Carter", "Roberts"
];

const territoryPrefixes = [
    "North", "South", "East", "West", "Central", "Upper", "Lower", "Greater", "Inner", "Outer",
    "Northern", "Southern", "Eastern", "Western", "Emerald", "Golden", "Crystal", "Silver",
    "Iron", "Shadow", "Grand", "Coastal", "Highland", "Lowland", "Metro", "Rural", "Peak",
    "River", "Lake", "Forest", "Prairie", "Urban", "Suburban", "Midnight", "Aurora", "Zenith",
    "Apex", "Summit", "Prime", "Beacon", "Vanguard"
];

const territorySuffixes = [
    "Sector", "Ridge", "District", "Bay", "Valley", "Heights", "Plains", "Coast", "Hills",
    "Woods", "Basin", "Canyon", "Plateau", "Territory", "Zone", "Region", "Province",
    "Frontier", "Reach", "Commons", "County", "Ward", "Precinct", "Parish", "Borough",
    "Township", "Municipality", "Tract", "Estate", "Manor", "Corridor", "Hub", "Node",
    "Nexus", "Perimeter", "Outpost", "Haven", "Sanctuary", "Preserve", "Garden"
];

const territoryCodes = [
    "01", "02", "03", "04", "05", "06", "07", "08", "09", "10",
    "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"
];

const partPrefixes = ["PN", "SKU", "ART", "REF", "MOD", "UNIT", "PROD", "ITEM", "CODE", "TYPE"];
const partAlpha = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const reasonPrefixes = ["RAD", "AI-RAD", "BI", "REJ", "QC", "RTN", "SCRAP"];
const reasonCategories = [
    "RADIATOR - AL/PL, Bolt On, Solder On",
    "ALL PARTS",
    "ENGINE COMPONENTS",
    "COOLING SYSTEM",
    "TRANSMISSION PARTS",
    "ELECTRICAL SYSTEM"
];
const reasonTimings = ["After Installation", "Before installation", "During shipping", "On Arrival"];
const reasonIssues = [
    "Leak-Core",
    "Leak-Tube to header",
    "Leak-Header to tank (Gasket/Seam)",
    "Manufactured wrong",
    "Recall",
    "Wrong label",
    "Damaged inside the box",
    "No part/pic from customer",
    "Canceled",
    "Physical Damage",
    "Functional Failure"
];

const productCategories = [
    "Radiators & Cooling",
    "Engine Components",
    "Transmission & Drivetrain",
    "Braking & Suspension",
    "Electrical & Ignition",
    "Filters & Maintenance",
    "Exhaust Systems",
    "Tools & Shop Supplies",
    "HVAC Systems",
    "Fuel Systems",
    "Body & Trim",
    "Performance Parts"
];

const warehouseCodes = ["00", "ED", "BC", "ON", "UT", "NY", "CA", "TX", "FL", "IL", "WA", "QC", "AB", "NS", "MB", "SK"];
const warehouseCities = [
    "Toronto", "Vancouver", "Montreal", "New York", "Los Angeles", "Chicago",
    "Houston", "Phoenix", "Miami", "Seattle", "Atlanta", "Dallas", "Denver", "Boston"
];

const warehouseTypes = ["Main Hub", "Regional Center", "Distribution Point", "Logistics Base", "Primary Warehouse"];

/** If a theme is strongly “cooling”, we intentionally skew nouns toward heat (fun contrast). */
function themedBanks(theme: Theme) {
    switch (theme) {
        case "cooling":
            return {
                adjectives: [...adjectivesGeneric, ...adjectivesHeat, ...adjectivesHVAC],
                nouns: [...nounsHeatPlural, ...nounsHVACPlural],
            };
        case "heating":
            return {
                adjectives: [...adjectivesGeneric, ...adjectivesCool, ...adjectivesHVAC],
                nouns: [...nounsCoolPlural, ...nounsHVACPlural],
            };
        case "hvac":
            return {
                adjectives: [...adjectivesGeneric, ...adjectivesHVAC],
                nouns: [...nounsHVACPlural],
            };
        case "plumbing":
            return {
                adjectives: [...adjectivesGeneric, ...adjectivesPlumb],
                nouns: [...nounsPlumbPlural],
            };
        case "electric":
            return {
                adjectives: [...adjectivesGeneric, ...adjectivesElectric],
                nouns: [...nounsElectricPlural],
            };
        default:
            return {
                adjectives: [...adjectivesGeneric],
                nouns: ["Works", "Industries", "Systems", "Group", "Holdings", "Outfit"],
            };
    }
}

/** --- Templates --- */
type TemplateFn = (rng: RNG, banks: { adjectives: string[]; nouns: string[] }) => string;

const templates: TemplateFn[] = [
    // 0: "Adjective Noun"
    (rng, b) => `${b.adjectives[rng.pickIndex(b.adjectives.length)]} ${b.nouns[rng.pickIndex(b.nouns.length)]}`,

    // 1: "Firstname's Noun"
    (rng, b) => `${firstNames[rng.pickIndex(firstNames.length)]}'s ${b.nouns[rng.pickIndex(b.nouns.length)]}`,

    // 2: "Adjective & Noun" (ampersand duo)
    (rng, b) => {
        const adj = b.adjectives[rng.pickIndex(b.adjectives.length)];
        const noun = b.nouns[rng.pickIndex(b.nouns.length)];
        return `${adj} & ${noun}`;
    },
];

/** --- Safety filter (avoid awkward endings, dup spaces, etc.) --- */
function cleanResult(s: string): string {
    const out = s.replace(/\s+/g, " ").trim();
    const res = titleCase(out.replace(/\'S\b/g, "'s")); // preserve possessive case nicely
    if (res.length <= 50) return res;
    return res.substring(0, 47) + "...";
}

function truncateToLimit(s: string, limit: number = 50): string {
    if (s.length <= limit) return s;
    return s.substring(0, limit - 3) + "...";
}

/** --- Public API --- */

/** Generates a deterministic business/brand name based on a theme detected from the source. */
export function generateBusinessName(sourceName: string): string {
    return getMemoized("business", sourceName, () => {
        const theme = detectTheme(sourceName);
        const banks = themedBanks(theme);

        // Two-stage seeding: source + theme for stability with slight thematic variance
        const baseSeed = fnv1a32(normalizeSource(sourceName));
        const seed = fnv1a32(theme + ":" + baseSeed.toString(16));

        const rng = new RNG(seed);
        const template = templates[seed % templates.length];
        return cleanResult(template(rng, banks));
    });
}

/** Generates a deterministic person name (First Last). */
export function generatePersonName(source: string): string {
    return getMemoized("person", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);
        const first = rng.pick(firstNames);
        const last = rng.pick(lastNames);
        return truncateToLimit(`${first} ${last}`);
    });
}

/** Generates a deterministic territory name (e.g., "North Ridge 01", "Emerald Valley"). */
export function generateTerritoryName(source: string): string {
    return getMemoized("territory", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);

        const prefix = rng.pick(territoryPrefixes);
        const suffix = rng.pick(territorySuffixes);
        const code = rng.pick(territoryCodes);

        const roll = rng.next();
        let result = "";
        if (roll < 0.3) {
            result = `${prefix} ${suffix}`;
        } else if (roll < 0.6) {
            result = `${suffix} ${code}`;
        } else {
            result = `${prefix} ${suffix} ${code}`;
        }
        return truncateToLimit(result);
    });
}

/** Generates a deterministic 11-digit vendor code (e.g., "00001234323"). */
export function generateVendorCode(source: string): string {
    return getMemoized("vendor", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);

        let code = "";
        for (let i = 0; i < 11; i++) {
            code += Math.floor(rng.next() * 10).toString();
        }
        return code;
    });
}

/** Generates a deterministic part number with various formats (e.g., "PN-123456", "SKU-ABC-123"). */
export function generatePartNumber(source: string): string {
    return getMemoized("part", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);

        const formatRoll = rng.next();

        const getRandAlpha = (len: number) => {
            let s = "";
            for (let i = 0; i < len; i++) s += partAlpha[rng.pickIndex(partAlpha.length)];
            return s;
        };
        const getRandNum = (len: number) => {
            let s = "";
            for (let i = 0; i < len; i++) s += Math.floor(rng.next() * 10).toString();
            return s;
        };

        if (formatRoll < 0.25) {
            // Format: PREFIX-NUMBER (PN-102938)
            return truncateToLimit(`${rng.pick(partPrefixes)}-${getRandNum(6)}`);
        } else if (formatRoll < 0.5) {
            // Format: ALPHA-NUMBER (XJ-4921)
            return truncateToLimit(`${getRandAlpha(2)}-${getRandNum(4)}`);
        } else if (formatRoll < 0.75) {
            // Format: BLOCK.BLOCK.BLOCK (10.200.30)
            return truncateToLimit(`${getRandNum(2)}.${getRandNum(3)}.${getRandNum(2)}`);
        } else {
            // Format: ALPHANUMERIC BLOCK (XJ49-K821)
            return truncateToLimit(`${getRandAlpha(2)}${getRandNum(2)}-${getRandAlpha(1)}${getRandNum(3)}`);
        }
    });
}

/** Generates a deterministic reason code (e.g., "RAD-01", "REJ-08"). */
export function generateReasonCode(source: string): string {
    return getMemoized("reasonCode", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);
        const prefix = rng.pick(reasonPrefixes);
        const num = (rng.pickIndex(20) + 1).toString().padStart(2, "0");
        return `${prefix}-${num}`;
    });
}

/** Generates a deterministic reason title based on common industry patterns. */
export function generateReasonTitle(source: string): string {
    return getMemoized("reasonTitle", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);

        const roll = rng.next();
        const category = rng.pick(reasonCategories);
        const issue = rng.pick(reasonIssues);
        const timing = rng.pick(reasonTimings);

        let result = "";
        if (roll < 0.4) {
            // Pattern: Category: Issue
            result = `${category}: ${issue}`;
        } else if (roll < 0.7) {
            // Pattern: Timing-Category-Issue
            result = `${timing}-${category.split(" - ")[0]}-${issue}`;
        } else {
            // Pattern: REJECTED BY Corp - Issue
            result = `REJ BY CORP. - ${issue}`;
        }
        return truncateToLimit(result);
    });
}

/** Generates a deterministic product category name. */
export function generateCategoryName(source: string): string {
    return getMemoized("category", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);
        return truncateToLimit(rng.pick(productCategories));
    });
}

/** Generates a deterministic warehouse name with codes (e.g., "ON - Main Hub", "Seattle 00"). */
export function generateWarehouseName(source: string): string {
    return getMemoized("warehouse", source, () => {
        const seed = fnv1a32(normalizeSource(source));
        const rng = new RNG(seed);

        const roll = rng.next();
        const city = rng.pick(warehouseCities);
        const code = rng.pick(warehouseCodes);
        const type = rng.pick(warehouseTypes);

        let result = "";
        if (roll < 0.33) {
            result = `${city} Warehouse ${code}`;
        } else if (roll < 0.66) {
            result = `${code} - ${type}`;
        } else {
            result = `${city} ${type}`;
        }
        return truncateToLimit(result);
    });
}

// Keep generateName as default export for backward compatibility
export const generateName = generateBusinessName;
export default generateName;